import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard, RedirectGuard, SettingsGuard, UnAuthGuard } from '@src/app/modules/auth';
import { FoldersComponent } from '@src/app/modules/telegram';
import { getPermissionGuardProps } from '@src/utils';

import { AppLayoutComponent } from './app-layout';
import { AssociationComponent } from './pages/association-page';
import { InformationComponent } from './pages/information-page';
import { LoginComponent } from './modules/login';
import { PageNotFoundComponent } from './pages/page-not-found';
import { PrivacyPageComponent } from './pages/privacy-page';
import { BirthdaysPageComponent } from './pages/birthdays-page';
import {
  NotificationsComponent,
  SettingsComponent,
  SupportPageComponent,
  LicensesPageComponent,
  ChangelogPageComponent,
  PreferencesComponent,
  ProfileComponent,
  LanguageComponent,
  ThemeComponent,
} from './modules/settings';
import { PolicyPageComponent } from './pages/policy-page';
import { RegistrationPageComponent } from './pages/registration-page';
import { LpSettingsComponent } from './modules/lp-settings/lp-settings.component';
import { DocumentsPageComponent } from './modules/settings/documents-page/documents-page.component';
import { CustomPageComponent } from './pages/custom-page';
import { ChatPublicPageComponent } from './pages/chat-public-page';

const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
    canActivate: [UnAuthGuard],
  },
  {
    path: 'not-found',
    component: PageNotFoundComponent,
  },
  {
    path: 'policy',
    component: PolicyPageComponent,
  },
  {
    path: 'privacy',
    component: PrivacyPageComponent,
  },
  {
    path: 'registration/:associationId',
    component: RegistrationPageComponent,
    canActivate: [SettingsGuard],
  },
  {
    path: 'support',
    component: SupportPageComponent,
  },
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        component: InformationComponent,
        canActivate: [RedirectGuard],
      },
      {
        path: 'info',
        component: InformationComponent,
        ...getPermissionGuardProps('pageInfoViewing'),
      },
      {
        path: 'chats',
        loadChildren: () => import('./pages/chats-page').then(module => module.ChatsPageModule),
        ...getPermissionGuardProps('pageChatsViewing'),
      },
      {
        path: 'chats-inner',
        loadChildren: () => import('./pages/chats-inner-page').then(module => module.ChatsInnerPageModule),
        ...getPermissionGuardProps('pageChatsInnerViewing'),
      },
      {
        path: 'chat-public',
        component: ChatPublicPageComponent,
        ...getPermissionGuardProps('pageChatPublicViewing'),
      },
      {
        path: 'events',
        loadChildren: () => import('./pages/events-page').then(module => module.EventsPageModule),
        ...getPermissionGuardProps('pageEventsViewing'),
      },
      {
        path: 'association',
        component: AssociationComponent,
        ...getPermissionGuardProps('pageAssociationViewing'),
      },
      {
        path: 'association-organisations',
        loadChildren: () => import('./pages/organisations-page').then(module => module.OrganisationsPageModule),
        ...getPermissionGuardProps('pageAssociationOrganisationsViewing'),
      },
      {
        path: 'association-users',
        loadChildren: () => import('./pages/users-page').then(module => module.UsersPageModule),
        ...getPermissionGuardProps('pageAssociationUsersViewing'),
      },
      {
        path: 'committees',
        loadChildren: () => import('./pages/committees-page').then(module => module.CommitteesPageModule),
        ...getPermissionGuardProps('pageCommitteesViewing'),
      },
      {
        path: 'organisation',
        loadChildren: () => import('./pages/organisation-page').then(module => module.OrganisationModule),
        ...getPermissionGuardProps('pageOrganisationViewing'),
      },
      {
        path: 'polls',
        loadChildren: () => import('./pages/polls-page').then(module => module.PollsModule),
        ...getPermissionGuardProps('pagePollsViewing'),
      },
      {
        path: 'newsletter',
        loadChildren: () => import('./pages/newsletter-page').then(module => module.NewsletterPageModule),
        ...getPermissionGuardProps('pageNewsletterViewing'),
      },
      {
        path: 'news-feed',
        loadChildren: () => import('./pages/news-feed-page').then(module => module.NewsFeedPageModule),
        ...getPermissionGuardProps('pageNewsFeedViewing'),
      },
      {
        path: 'settings',
        component: SettingsComponent,
        ...getPermissionGuardProps('pageSettingsViewing'),
        children: [
          {
            path: 'profile',
            component: ProfileComponent,
            ...getPermissionGuardProps('pageProfileViewing'),
          },
          {
            path: 'folders',
            component: FoldersComponent,
            ...getPermissionGuardProps('pageChatsViewing'),
          },
          {
            path: 'notifications',
            component: NotificationsComponent,
            ...getPermissionGuardProps('pageSettingsNotificationsViewing'),
          },
          {
            path: 'administration',
            loadChildren: () => import('./modules/settings/administration').then(module => module.AdministrationModule),
            ...getPermissionGuardProps('pageAdministrationViewing'),
          },
          {
            path: 'language',
            component: LanguageComponent,
          },
          {
            path: 'theme',
            component: ThemeComponent,
          },
          {
            path: 'licenses',
            component: LicensesPageComponent,
          },
          {
            path: 'changelog',
            component: ChangelogPageComponent,
          },
          {
            path: 'support',
            component: SupportPageComponent,
          },
          {
            path: 'documents',
            component: DocumentsPageComponent,
          },
          {
            path: 'preferences',
            component: PreferencesComponent,
            ...getPermissionGuardProps('pageSettingsPreferencesViewing'),
          },
        ],
      },
      {
        path: 'loyalty',
        loadChildren: () => import('./pages/loyalty-page').then(module => module.LoyaltyPageModule),
        ...getPermissionGuardProps('pageLoyaltyViewing'),
      },
      {
        path: 'lp-settings',
        component: LpSettingsComponent,
        ...getPermissionGuardProps('showOnlyLoyaltyProgram'),
        children: [
          {
            path: 'profile',
            component: ProfileComponent,
            ...getPermissionGuardProps('pageProfileViewing'),
          },
          {
            path: 'notifications',
            component: NotificationsComponent,
            ...getPermissionGuardProps('pageSettingsNotificationsViewing'),
          },
          {
            path: 'language',
            component: LanguageComponent,
          },
          {
            path: 'theme',
            component: ThemeComponent,
          },
          {
            path: 'support',
            component: SupportPageComponent,
          },
          {
            path: 'documents',
            component: DocumentsPageComponent,
          },
        ],
      },
      {
        path: 'cooperation-chains',
        loadChildren: () =>
          import('./pages/cooperation-chains-page').then(module => module.CooperationChainsPageModule),
        ...getPermissionGuardProps('pageCooperationChainsViewing'),
      },
      {
        path: 'birthdays',
        component: BirthdaysPageComponent,
        ...getPermissionGuardProps('pageBirthdaysViewing'),
      },
      { path: 'pages/:pageId', component: CustomPageComponent },
      {
        path: '**',
        component: PageNotFoundComponent,
      },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      anchorScrolling: 'enabled',
      onSameUrlNavigation: 'reload',
      scrollPositionRestoration: 'disabled', // INFO: disabled, т.к. используем ScrollPositionRestorationModule
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
